import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, Pencil, Users } from "lucide-react";
import type { CrewDetail } from "@crewledger/shared/schemas";
import { CommandCenterCard, EmptyState, PageHeader } from "@/components/design";
import { Button } from "@/components/ui/button";
import { fetchCrew } from "./api";
import { useCrewPermissions } from "./use-crew-permissions";

export function CrewDetailPage() {
  const { crewId } = useParams();
  const navigate = useNavigate();
  const { canWrite } = useCrewPermissions();
  const [crew, setCrew] = useState<CrewDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!crewId) return;
    fetchCrew(crewId)
      .then((res) => { setCrew(res.data); })
      .catch((err: unknown) => { setError(err instanceof Error ? err.message : "Failed to load crew"); })
      .finally(() => { setLoading(false); });
  }, [crewId]);

  if (error && !crew) {
    return (
      <EmptyState
        icon={Users}
        title="Crew not found"
        description={error}
        actionLabel="Back to crews"
        onAction={() => navigate("/crews")}
      />
    );
  }

  if (loading || !crew) {
    return <div className="h-96 animate-pulse rounded-xl border bg-card-white" />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={crew.name}
        description={crew.description ?? "Crew template"}
        badge={
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
              crew.active ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-600"
            }`}
          >
            {crew.active ? "Active" : "Inactive"}
          </span>
        }
        actions={
          crew.canEdit && canWrite ? (
            <Link to={`/crews/${crew.id}/edit`}>
              <Button variant="outline" size="sm">
                <Pencil className="mr-1.5 h-4 w-4" />
                Edit
              </Button>
            </Link>
          ) : undefined
        }
      />

      {crew.inactiveMemberCount > 0 ? (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          {crew.inactiveMemberCount} inactive member{crew.inactiveMemberCount === 1 ? "" : "s"} — daily entry will warn when this crew is applied.
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-3">
        <CommandCenterCard title="Details" icon={Users} className="lg:col-span-1">
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-xs uppercase tracking-wider text-muted-foreground">Site</dt>
              <dd>{crew.siteName ? `${crew.siteCode} — ${crew.siteName}` : "All sites"}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-muted-foreground">Members</dt>
              <dd className="font-semibold tabular-nums">{crew.memberCount}</dd>
            </div>
          </dl>
        </CommandCenterCard>

        <CommandCenterCard title="Members & defaults" className="lg:col-span-2">
          {crew.members.length === 0 ? (
            <p className="text-sm text-muted-foreground">No members configured.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                    <th className="pb-2">Worker</th>
                    <th className="pb-2">Job</th>
                    <th className="pb-2">Cost</th>
                    <th className="pb-2">Equipment</th>
                  </tr>
                </thead>
                <tbody>
                  {crew.members.map((m) => (
                    <tr key={m.id} className="border-b last:border-0">
                      <td className="py-2">
                        <p className="font-medium text-navy-deep">
                          {m.employeeId} — {m.workerName}
                        </p>
                        {m.workerStatus === "INACTIVE" ? (
                          <p className="text-xs text-amber-700">Inactive</p>
                        ) : null}
                      </td>
                      <td className="py-2 font-mono">{m.defaultJobCode ?? "—"}</td>
                      <td className="py-2 font-mono">{m.defaultCostCode ?? "—"}</td>
                      <td className="py-2">{m.defaultEquipmentName ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CommandCenterCard>
      </div>
    </div>
  );
}
